import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckSquare, Download, RefreshCw, Square } from "lucide-react";
import "./ExportData.scss";
import { getAPIUrl } from "../api/apiUtils";

const SOURCES = [
  { id: "vk", title: "ВК" },
  { id: "tg", title: "Telegram" },
  { id: "news", title: "Новости" },
  { id: "youtube", title: "YouTube" },
];

const STATUS_LABELS = {
  queued: "В очереди",
  running: "Выполняется",
  completed: "Готов",
  failed: "Ошибка",
  expired: "Истек",
};

const STATUS_VARIANTS = {
  queued: "muted",
  running: "info",
  completed: "success",
  failed: "danger",
  expired: "muted",
};

const today = () => new Date().toISOString().slice(0, 10);

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatSize = (value) => {
  const size = Number(value || 0);
  if (!size) return "-";
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} МБ`;
  if (size >= 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${size} Б`;
};

const ExportData = () => {
  const [source, setSource] = useState("vk");
  const [channels, setChannels] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(today());
  const [includeComments, setIncludeComments] = useState(false);
  const [format, setFormat] = useState("csv");
  const [jobs, setJobs] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [downloadingJobId, setDownloadingJobId] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("create");

  const selectedChannels = useMemo(
    () => channels.filter((channel) => selectedIds.includes(channel.id)),
    [channels, selectedIds]
  );

  const hasActiveJobs = useMemo(
    () => jobs.some((job) => ["queued", "running"].includes(job.status)),
    [jobs]
  );

  const allSelected = channels.length > 0 && selectedIds.length === channels.length;

  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const response = await axios.get(`${getAPIUrl()}/export_data/jobs`);
      setJobs(response.data.jobs || []);
    } catch (requestError) {
      console.error("Failed to load export jobs", requestError);
      setError("Не удалось загрузить список экспортов");
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const loadChannels = useCallback(async (nextSource = source) => {
    setLoadingChannels(true);
    setError("");
    try {
      const response = await axios.get(`${getAPIUrl()}/source_data/channels`, {
        params: { source: nextSource },
      });
      const nextChannels = response.data.channels || [];
      setChannels(nextChannels);
      setSelectedIds(nextChannels.map((channel) => channel.id));
    } catch (requestError) {
      console.error("Failed to load export channels", requestError);
      setChannels([]);
      setSelectedIds([]);
      setError("Не удалось загрузить источники");
    } finally {
      setLoadingChannels(false);
    }
  }, [source]);

  useEffect(() => {
    loadChannels(source);
  }, [loadChannels, source]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (!hasActiveJobs) return undefined;
    const timer = setInterval(loadJobs, 5000);
    return () => clearInterval(timer);
  }, [hasActiveJobs, loadJobs]);

  const handleSourceChange = (nextSource) => {
    if (nextSource === source) return;

    setSource(nextSource);
    setChannels([]);
    setSelectedIds([]);
    setError("");
  };

  const toggleChannel = (channelId) => {
    setSelectedIds((current) =>
      current.includes(channelId)
        ? current.filter((id) => id !== channelId)
        : [...current, channelId]
    );
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : channels.map((channel) => channel.id));
  };

  const createExportJob = async () => {
    if (!selectedChannels.length || creatingJob) return;

    setCreatingJob(true);
    setError("");
    try {
      await axios.post(`${getAPIUrl()}/export_data/jobs`, {
        source,
        channels: selectedChannels,
        start_date: startDate || null,
        end_date: endDate || null,
        include_comments: includeComments,
        format,
      });
      await loadJobs();
      setActiveTab("jobs");
    } catch (requestError) {
      console.error("Failed to create export job", requestError);
      setError(requestError?.response?.data?.error || "Не удалось поставить экспорт в очередь");
    } finally {
      setCreatingJob(false);
    }
  };

  const downloadJob = async (job) => {
    if (!job?.job_id || downloadingJobId) return;

    setDownloadingJobId(job.job_id);
    setError("");
    try {
      const response = await axios.get(`${getAPIUrl()}/export_data/jobs/${job.job_id}/download`, {
        responseType: "blob",
      });
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || job.download_name || job.file_name || `${job.job_id}.${job.format || "csv"}`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = decodeURIComponent(filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      console.error("Failed to download export job", requestError);
      setError("Не удалось скачать файл экспорта");
    } finally {
      setDownloadingJobId("");
    }
  };

  return (
    <main className="export-data">
      <div className="export-data-header">
        <h1>Экспорт данных</h1>
        <button type="button" className="export-data-refresh" onClick={loadJobs} disabled={loadingJobs}>
          <RefreshCw size={16} />
          Обновить
        </button>
      </div>

      <div className="export-data-page-tabs" role="tablist" aria-label="Раздел экспорта">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "create"}
          className={`export-data-page-tab ${activeTab === "create" ? "export-data-page-tab--active" : ""}`}
          onClick={() => setActiveTab("create")}
        >
          Новый экспорт
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "jobs"}
          className={`export-data-page-tab ${activeTab === "jobs" ? "export-data-page-tab--active" : ""}`}
          onClick={() => setActiveTab("jobs")}
        >
          Задания экспорта
        </button>
      </div>

      {error ? <div className="export-data-error">{error}</div> : null}

      {activeTab === "create" ? (
        <>
          <section className="export-data-panel">
            <div className="export-data-source-tabs" aria-label="Сервис">
              {SOURCES.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`export-data-tab ${source === item.id ? "export-data-tab--active" : ""}`}
                  onClick={() => handleSourceChange(item.id)}
                  disabled={creatingJob}
                >
                  {item.title}
                </button>
              ))}
            </div>

            <div className="export-data-controls">
              <label className="export-data-field">
                <span>С</span>
                <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} disabled={creatingJob} />
              </label>
              <label className="export-data-field">
                <span>По</span>
                <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} disabled={creatingJob} />
              </label>
              <label className="export-data-check">
                <input
                  type="checkbox"
                  checked={includeComments}
                  onChange={(event) => setIncludeComments(event.target.checked)}
                  disabled={creatingJob}
                />
                <span>Выгружать комментарии</span>
              </label>
              <div className="export-data-format" role="group" aria-label="Формат">
                <button type="button" className={format === "csv" ? "active" : ""} onClick={() => setFormat("csv")} disabled={creatingJob}>
                  CSV
                </button>
                <button type="button" className={format === "json" ? "active" : ""} onClick={() => setFormat("json")} disabled={creatingJob}>
                  JSON
                </button>
              </div>
            </div>
          </section>

          <section className="export-data-panel">
            <div className="export-data-panel-header">
              <h2>Источники</h2>
              <button type="button" className="export-data-select-all" onClick={toggleAll} disabled={loadingChannels || creatingJob || !channels.length}>
                {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                {allSelected ? "Снять все" : "Выбрать все"}
              </button>
            </div>

            {loadingChannels ? <div className="export-data-empty">Загрузка...</div> : null}
            {!loadingChannels && !channels.length ? <div className="export-data-empty">Нет источников</div> : null}
            {!loadingChannels && channels.length ? (
              <div className="export-data-channels">
                {channels.map((channel) => {
                  const checked = selectedIds.includes(channel.id);
                  return (
                    <label key={channel.id} className={`export-data-channel ${checked ? "export-data-channel--selected" : ""}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleChannel(channel.id)} disabled={creatingJob} />
                      <span>
                        <strong>{channel.title || channel.id}</strong>
                        {channel.subtitle ? <small>{channel.subtitle}</small> : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : null}
          </section>

          <div className="export-data-footer">
            <div className="export-data-summary">
              <strong>{selectedChannels.length.toLocaleString("ru-RU")}</strong>
              <span>выбрано</span>
              {includeComments ? <em>комментарии включены</em> : null}
            </div>
            <button type="button" className="export-data-run" onClick={createExportJob} disabled={!selectedChannels.length || creatingJob}>
              <Download size={18} />
              {creatingJob ? "Постановка..." : "Поставить в очередь"}
            </button>
          </div>
        </>
      ) : (
        <section className="export-data-panel">
          <div className="export-data-panel-header">
            <h2>Задания экспорта</h2>
            <span className="export-data-count">{jobs.length.toLocaleString("ru-RU")}</span>
          </div>

          {!jobs.length ? <div className="export-data-empty">Готовых экспортов пока нет</div> : null}
          {jobs.length ? (
            <div className="export-data-jobs">
              {jobs.map((job) => {
                const status = job.status || "queued";
                const progress = job.progress || {};
                const channelsCount = job.channels?.length || 0;
                return (
                  <div key={job.job_id} className="export-data-job">
                    <div className="export-data-job-main">
                      <span className={`export-data-status export-data-status--${STATUS_VARIANTS[status] || "muted"}`}>
                        {STATUS_LABELS[status] || status}
                      </span>
                      <strong>{SOURCES.find((item) => item.id === job.source)?.title || job.source}</strong>
                      <span>{channelsCount.toLocaleString("ru-RU")} источн.</span>
                      <span>{job.format?.toUpperCase()}</span>
                      {job.include_comments ? <span>с комментариями</span> : <span>без комментариев</span>}
                    </div>
                    <div className="export-data-job-meta">
                      <span>создан: {formatDateTime(job.created_at)}</span>
                      <span>истекает: {formatDateTime(job.expires_at)}</span>
                      <span>размер: {formatSize(job.file_size)}</span>
                      {progress.message ? <span>{progress.message}</span> : null}
                      {job.error ? <span className="export-data-job-error">{job.error}</span> : null}
                    </div>
                    <button
                      type="button"
                      className="export-data-job-download"
                      onClick={() => downloadJob(job)}
                      disabled={status !== "completed" || downloadingJobId === job.job_id}
                    >
                      <Download size={16} />
                      Скачать
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
        </section>
      )}
    </main>
  );
};

export default ExportData;
